// Paste import for Flashcards. Three ways to read a pasted block:
//   auto    - parseBulk, the free-form "term - definition" list (one card per line)
//   quizlet - the text Quizlet's Export box gives you, with its separator choices
//   anki    - an Anki "Notes in Plain Text" export, header lines and HTML included
// Everything here is plain string work. Nothing is fetched, and HTML from Anki is
// turned into plain text; cards are only ever shown with textContent.

// Card and deck ids. A paste can mint hundreds in the same millisecond, so the id is
// 96 random bits from crypto, not the clock. The fallback only runs without crypto.
const hex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
export function uid() {
	const c = globalThis.crypto;
	try { if (c && c.getRandomValues) return "c" + hex(c.getRandomValues(new Uint8Array(12))); } catch (e) {}
	let s = "c" + Date.now().toString(36);
	for (let i = 0; i < 4; i++) s += Math.floor(Math.random() * 0x100000000).toString(36);
	return s;
}

// FNV-1a over a string, two lanes, so the repair below picks the same id on every device.
function stableId(text) {
	let a = 0x811c9dc5, b = 0x01000193 ^ 0x5bd1e995;
	for (let i = 0; i < text.length; i++) {
		const ch = text.charCodeAt(i);
		a = Math.imul(a ^ ch, 0x01000193) >>> 0;
		b = Math.imul(b ^ ch, 0x5bd1e995) >>> 0;
	}
	return "r" + a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0");
}

// Older builds could give two cards in a deck the same id. The first card keeps it,
// later ones get an id made from deck id + old id + position, so every device that
// loads the same store makes the same fix. Missing ids are filled the same way.
// Changes the store in place and returns true when anything changed.
export function repairCardIds(db) {
	let changed = false;
	for (const d of (db && Array.isArray(db.decks) ? db.decks : [])) {
		if (!d || !Array.isArray(d.cards)) continue;
		const seen = new Set();
		for (const c of d.cards) if (c && typeof c.id === "string" && c.id) seen.add(c.id);
		const kept = new Set();
		d.cards.forEach((c, i) => {
			if (!c || typeof c !== "object") return;
			const old = typeof c.id === "string" ? c.id : "";
			if (old && !kept.has(old)) { kept.add(old); return; }
			let n = 0, id;
			do id = stableId(String(d.id) + "\u0000" + old + "\u0000" + i + "\u0000" + n++); while (seen.has(id));
			seen.add(id); kept.add(id);
			c.id = id;
			changed = true;
		});
	}
	return changed;
}

// Split a pasted block into {front,back} cards. Per line, the term/definition
// separator is (first match wins): a tab, a space-padded dash (- – —), or a colon
// or equals followed by a space ("term: def" / "term = def"). Requiring a trailing
// space on : and = keeps hyphenated terms and "https://" style text from splitting.
// Blank/unsplittable lines are skipped.
export function parseBulk(text) {
	const out = [];
	(text || "").split(/\r?\n/).forEach((line) => {
		const t = line.trim();
		if (!t) return;
		const m = t.match(/^(.+?)\t+(.+)$/) || t.match(/^(.+?)\s+[-–—]\s+(.+)$/) || t.match(/^(.+?)\s*[:=]\s+(.+)$/);
		if (!m) return;
		const front = m[1].trim(), back = m[2].trim();
		if (front && back) out.push({ id: uid(), front, back });
	});
	return out;
}

// Typed custom separators may spell a tab or line break as \t or \n.
const unescape = (s) => String(s == null ? "" : s).replace(/\\t/g, "\t").replace(/\\n/g, "\n");
const tidy = (s) => s.split("\n").map((l) => l.trim()).join("\n").replace(/\n{3,}/g, "\n\n").trim();

// Quizlet's Export box: "Between term and definition" is Tab, Comma or custom text,
// "Between rows" is New line, Semicolon or custom text. A row splits at the first
// term separator, so a definition can still contain that character.
export const QUIZLET_TERM = { tab: "\t", comma: "," };
export const QUIZLET_ROW = { newline: "\n", semicolon: ";" };
const quizletRowSep = (opts) => (opts.row in QUIZLET_ROW ? QUIZLET_ROW[opts.row] : unescape(opts.rowCustom) || "\n");
export function parseQuizlet(text, opts = {}) {
	const termSep = opts.term in QUIZLET_TERM ? QUIZLET_TERM[opts.term] : unescape(opts.termCustom) || "\t";
	const rowSep = quizletRowSep(opts);
	const src = String(text || "").replace(/\r\n?/g, "\n");
	const out = [];
	for (const row of src.split(rowSep)) {
		const at = row.indexOf(termSep);
		if (at < 0) continue;
		const front = tidy(row.slice(0, at)), back = tidy(row.slice(at + termSep.length));
		if (front && back) out.push({ id: uid(), front, back });
	}
	return out;
}

// --- Anki --------------------------------------------------------------------

const NAMED = { nbsp: " ", amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", ensp: " ", emsp: " ", thinsp: " ", hellip: "\u2026", ndash: "\u2013", mdash: "\u2014", lsquo: "\u2018", rsquo: "\u2019", ldquo: "\u201c", rdquo: "\u201d", middot: "\u00b7", bull: "\u2022", times: "\u00d7", divide: "\u00f7", deg: "\u00b0", plusmn: "\u00b1", micro: "\u00b5", copy: "\u00a9", reg: "\u00ae", trade: "\u2122", euro: "\u20ac", pound: "\u00a3", cent: "\u00a2", sect: "\u00a7", para: "\u00b6", laquo: "\u00ab", raquo: "\u00bb", larr: "\u2190", rarr: "\u2192", uarr: "\u2191", darr: "\u2193", harr: "\u2194", le: "\u2264", ge: "\u2265", ne: "\u2260", asymp: "\u2248", infin: "\u221e", radic: "\u221a", sum: "\u2211", pi: "\u03c0", alpha: "\u03b1", beta: "\u03b2", gamma: "\u03b3", delta: "\u03b4", theta: "\u03b8", lambda: "\u03bb", mu: "\u03bc", sigma: "\u03c3", omega: "\u03c9", Delta: "\u0394", Sigma: "\u03a3", Omega: "\u03a9", sup2: "\u00b2", sup3: "\u00b3", frac12: "\u00bd", frac14: "\u00bc", frac34: "\u00be" };
function decodeEntities(s) {
	// One pass, so "&amp;lt;" becomes the text "&lt;" and never a "<".
	return s.replace(/&(#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[a-zA-Z][a-zA-Z0-9]{1,31});/g, (all, name) => {
		if (name[0] === "#") {
			const n = name[1] === "x" || name[1] === "X" ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
			if (!(n > 0) || n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff)) return "";
			return n === 0xa0 ? " " : String.fromCodePoint(n);
		}
		return Object.prototype.hasOwnProperty.call(NAMED, name) ? NAMED[name] : all;
	});
}

// Cloze deletions, {{c1::answer}} or {{c1::answer::hint}}: each one is replaced by
// fn(answer, hint). Done with indexOf rather than a lazy pattern so a field full of
// unclosed "{{c1::" stays linear.
function replaceCloze(s, fn) {
	const head = /\{\{c\d+::/y;
	let out = "", i = 0, from = 0;
	for (;;) {
		const at = s.indexOf("{{c", from);
		if (at < 0) break;
		head.lastIndex = at;
		if (!head.test(s)) { from = at + 1; continue; }
		const start = head.lastIndex, end = s.indexOf("}}", start);
		if (end < 0) break; // nothing after this can close either
		const body = s.slice(start, end), cut = body.indexOf("::");
		out += s.slice(i, at) + (cut < 0 ? fn(body) : fn(body.slice(0, cut), body.slice(cut + 2)));
		i = from = end + 2;
	}
	return out + s.slice(i);
}

// [sound:file.mp3] tags. A "[sound:" with no "]" after it ends the search, so a field
// full of unclosed ones stays linear. Matched in place at each "[" rather than on a
// lowercased copy, whose indexes can drift ("\u0130" lowercases to two code units).
function dropSounds(s) {
	const head = /\[sound:/iy;
	let out = "", i = 0, from = 0;
	for (;;) {
		const at = s.indexOf("[", from);
		if (at < 0) break;
		head.lastIndex = at;
		if (!head.test(s)) { from = at + 1; continue; }
		const end = s.indexOf("]", at + 7);
		if (end < 0) break;
		out += s.slice(i, at);
		i = from = end + 1;
	}
	return out + s.slice(i);
}

const BLOCK_TAGS = new Set(["div", "p", "li", "ul", "ol", "h1", "h2", "h3", "h4", "h5", "h6", "tr", "table", "blockquote", "pre", "section", "article", "hr"]);
const tagStart = (c) => (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || c === 47 || c === 33; // A-Z a-z / !

// One left-to-right pass over the field. A "<" followed by a letter, "/" or "!" opens a
// tag that runs to the next ">"; with no ">" left the rest of the field goes. <br> is a
// line break, block edges are \ue000, table cells a space, and any other tag nothing.
function stripTags(s) {
	const out = [];
	let i = 0;
	const tag = (p) => {
		const gt = s.indexOf(">", p);
		if (gt < 0) return s.length;
		const close = s[p] === "/";
		const name = /^[A-Za-z0-9_]*/.exec(s.slice(close ? p + 1 : p, Math.min(gt, p + 12)))[0].toLowerCase();
		if (name === "br" && !close) out.push("\n");
		else if (BLOCK_TAGS.has(name)) out.push("\ue000");
		else if (name === "td" || name === "th") out.push(" ");
		return gt + 1;
	};
	while (i < s.length) {
		const lt = s.indexOf("<", i);
		if (lt < 0) { out.push(s.slice(i)); break; }
		if (lt > i) out.push(s.slice(i, lt));
		if (!tagStart(s.charCodeAt(lt + 1))) { out.push("<"); i = lt + 1; continue; }
		i = tag(lt + 1);
	}
	return out.join("");
}

// Spaces or tabs at the end of a string, trimmed with a loop (a [ \t]+$ pattern is
// quadratic on a long run of spaces that isn't at the end).
const trimEndSpaces = (p) => { let e = p.length; while (e && (p[e - 1] === " " || p[e - 1] === "\t")) e--; return p.slice(0, e); };

// Anki field HTML -> plain text. Line-ish tags become line breaks, script/style
// bodies, images, sound tags and comments are dropped, every other tag is removed,
// then entities are decoded once. The result is text, never markup. Every step is
// linear in the field length, so a huge or hostile paste can't hang the page.
export function ankiToText(html) {
	let s = String(html || "").replace(/\ue000/g, "");
	s = dropSounds(s);
	// Cloze deletions read as their answer: {{c1::Paris::capital}} -> Paris
	s = replaceCloze(s, (answer) => answer);
	// Repeat until nothing changes, so a tag split by another ("<<b></b>img ...>") goes too.
	for (let prev = "", n = 0; prev !== s && n < 20; n++) {
		prev = s;
		s = s.replace(/<!--[\s\S]*?(-->|$)/g, "");
		s = s.replace(/<(script|style|template|noscript|iframe|object|svg|math)\b[\s\S]*?(<\/\1\s*>|$)/gi, "");
		s = stripTags(s);
	}
	// Block edges mark a line break; touching edges ("</div><div>") count once.
	const parts = s.split(/\ue000[\ue000\s]*/);
	s = parts.map((p, k) => (k < parts.length - 1 ? trimEndSpaces(p) : p)).join("\n");
	s = decodeEntities(s).replace(/\u00a0/g, " ").replace(/[ \t\f\v]+/g, " ");
	return tidy(s);
}

const ANKI_SEPS = { tab: "\t", comma: ",", semicolon: ";", space: " ", pipe: "|", colon: ":" };

// Fields split on sep. A field that starts with a double quote runs to the matching
// quote ("" inside is a literal quote), so it may hold the separator or line breaks.
function splitRecords(text, sep) {
	const rows = [];
	let row = [], field = "", i = 0, quoted = false, atStart = true;
	const endField = () => { row.push(field); field = ""; atStart = true; };
	const endRow = () => { endField(); rows.push(row); row = []; };
	while (i < text.length) {
		const ch = text[i];
		if (quoted) {
			if (ch === "\"") {
				if (text[i + 1] === "\"") { field += "\""; i += 2; continue; }
				quoted = false; i++; continue;
			}
			field += ch; i++; continue;
		}
		if (atStart && ch === "\"") { quoted = true; atStart = false; i++; continue; }
		if (text.startsWith(sep, i)) { endField(); i += sep.length; continue; }
		if (ch === "\n") { endRow(); i++; continue; }
		field += ch; atStart = false; i++;
	}
	if (field || row.length) endRow();
	return rows;
}

const CLOZE = /\{\{c\d+::/;

export function parseAnki(text) {
	const src = String(text || "").replace(/^\ufeff/, "").replace(/\r\n?/g, "\n");
	const lines = src.split("\n");
	let sep = "\t", html = true;
	const skip = new Set(); // 0-based columns that hold note type, deck, tags or guid
	let n = 0;
	for (; n < lines.length; n++) {
		const m = lines[n].match(/^#([a-z ]+):(.*)$/i);
		if (!m) { if (lines[n].trim() === "" && n < lines.length - 1) continue; break; }
		const key = m[1].trim().toLowerCase(), val = m[2].trim();
		if (key === "separator") sep = ANKI_SEPS[val.toLowerCase()] || (val.length === 1 ? val : "\t");
		else if (key === "html") html = val.toLowerCase() !== "false";
		else if (/^(notetype|deck|tags|guid) column$/.test(key) && /^\d+$/.test(val) && +val > 0) skip.add(+val - 1);
	}
	const body = lines.slice(n).join("\n");
	const clean = html ? ankiToText : tidy;
	const out = [];
	for (const rec of splitRecords(body, sep)) {
		const raw = rec.filter((_, i) => !skip.has(i));
		let front = clean(raw[0] || ""), back = clean(raw[1] || "");
		// A cloze note: the front hides each deletion, the back shows the whole text (plus any extra).
		if (html && CLOZE.test(raw[0] || "")) {
			front = clean(replaceCloze(raw[0], (_, hint) => "[" + (hint || "...") + "]"));
			back = [clean(raw[0]), back].filter(Boolean).join("\n");
		}
		if (front && back) out.push({ id: uid(), front, back });
	}
	return out;
}

export function parseImport(text, preset, opts) {
	if (preset === "quizlet") return parseQuizlet(text, opts);
	if (preset === "anki") return parseAnki(text);
	return parseBulk(text);
}

// The live count under the paste box. A big paste is only counted up to PREVIEW_CHARS,
// cut back to the last whole row, and comes back as { count, partial: true }; the count
// is then a floor. Importing always reads the whole text.
export const PREVIEW_CHARS = 100000;
export function previewImport(text, preset, opts = {}) {
	const src = String(text || "");
	if (src.length <= PREVIEW_CHARS) return { count: parseImport(src, preset, opts).length, partial: false };
	const head = src.slice(0, PREVIEW_CHARS);
	const sep = preset === "quizlet" ? quizletRowSep(opts) : "\n";
	const cut = head.lastIndexOf(sep);
	return { count: parseImport(cut > 0 ? head.slice(0, cut) : head, preset, opts).length, partial: true };
}
